import baseStyled, { css as baseCss, ThemedCssFunction, ThemedStyledInterface } from 'styled-components';

import { Card } from '@plasma/ui.display.card';
import Button from '@plasma/ui.general.button';
import DatePicker from '@plasma/ui.input.date-picker';
import { Select } from '@plasma/ui.input.select';

import { ProductionFigureTheme } from './themes/types';

const styled = baseStyled as ThemedStyledInterface<ProductionFigureTheme>;
const css = baseCss as ThemedCssFunction<ProductionFigureTheme>;

export const ProductionFigure = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background-color: #f8fafc;
`;

export const NormalPlasmaButton = styled(Button)`
  ${({ theme }) => css`
    background-color: ${theme.mode.productionFigure.normalButtonBGColor} !important;
    border: ${theme.mode.productionFigure.normalButtonBorderColor} !important;
  `}
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  }
`;

export const StyledCard = styled(Card)`
  box-sizing: border-box;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  transition: box-shadow 0.3s ease;
  overflow: hidden;

  ${({ theme }) => css`
    background: ${theme.mode.productionFigure.cardBGColor};
    border: 1px solid ${theme.mode.productionFigure.cardBorderColor || '#e2e8f0'};
  `}

  &:hover {
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  }
`;

export const ProfileHeaderContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.mode.productionFigure.profileHeaderBGColor};
    color: ${theme.mode.productionFigure.profileHeaderFontColor};
  `}
  font-weight: 700;
  font-size: 16px;
  margin-right: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  letter-spacing: 0.5px;
`;

export const ProfileControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 5px 0;
`;

export const RemainingTons = styled.div<{ $isUsedUp?: boolean }>`
  color: ${({ $isUsedUp }) => ($isUsedUp ? '#2563eb' : '#16a34a')}; /* blue if used up, green otherwise */
  font-weight: 700;
  font-size: 14px;
  padding: 4px 0px;
  margin-bottom: 4px;
`;

export const PlanEditAndTotalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin: 0 2vw 2px 2vw;
`;

export const PlanEditContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  padding: 8px 12px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
  ${({ theme }) => css`
    background-color: ${theme.mode.productionFigure.planContainerBGColor || '#ffffff'};
  `}
`;

export const PlanEditLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
`;

export const PlanEditRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

export const PlanEditButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`;

export const PlannedTotalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .total-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

export const DateSelectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
`;

export const SelectStyled = styled(Select)`
  min-width: 120px;

  .ant-select-selector {
    border-radius: 6px !important;
  }

  .ant-select-clear {
    margin-right: 1vw;
  }
`;

export const DatePickerContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const DatePickerStyled = styled(DatePicker)`
  border-radius: 6px;
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 380px);
  justify-content: center;
  gap: 15px;
  padding: 20px 2vw 5px 2vw; /* 20px top padding provides the vertical gap */
  max-height: 60vh;
  overflow-y: auto;

  & > div {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

export const ProfileItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  margin-bottom: 2px;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
  border-radius: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f5f9;
  }

  .badges {
    display: flex;
    gap: 8px;
    align-items: center;
    flex: 1;

    .badge {
      flex: 1; /* Make all three badges exactly equal length */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 8px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      border: 1px solid rgba(0,0,0,0.1);
    }
  }

  .badge-dim { background-color: #dcfce7; color: #166534; }
  .badge-percent { background-color: #dbeafe; color: #1e3a8a; }
  .badge-tonnage { background-color: #e0e7ff; color: #3730a3; }
`;
